import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Save, Loader2, Stethoscope, ListChecks, Pill } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });

export default function CapabilitiesPage() {
  const [caps, setCaps] = useState(null);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    try { setCaps((await ax.get('/api/tenant-admin/capabilities')).data); }
    catch (_) { /* */ }
  };
  useEffect(() => { refresh(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await ax.patch('/api/tenant-admin/capabilities', {
        doctor_can_do_reception: caps.doctor_can_do_reception,
        doctor_can_dispense: caps.doctor_can_dispense,
        pharmacy_mode: caps.pharmacy_mode,
      });
      toast.success('Capabilities saved');
    } catch (e) { toast.error('Failed to save'); }
    setSaving(false);
  };

  if (!caps) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;

  const Row = ({ icon: Icon, title, desc, value, onChange, testid }) => (
    <div className="flex items-start gap-4 p-4 border rounded-lg">
      <Icon className="h-5 w-5 text-blue-700 mt-0.5" />
      <div className="flex-1">
        <div className="font-medium text-zinc-900">{title}</div>
        <div className="text-xs text-zinc-500 mt-0.5">{desc}</div>
      </div>
      <Switch checked={!!value} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );

  return (
    <div className="p-8 max-w-3xl">
      <h1 className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope' }}
          data-testid="ta-caps-title">
        Clinic capabilities
      </h1>
      <p className="text-sm text-zinc-500 mt-1">
        Choose what each role can do at this clinic. Individual doctors can
        be overridden in <a href="/admin/users" className="text-blue-700 underline">Users</a>.
      </p>

      <div className="mt-6 space-y-3">
        <Row
          icon={ListChecks}
          title="Doctors can also do reception"
          desc="Register patients, start a session, generate queue numbers, collect consultation fee."
          value={caps.doctor_can_do_reception}
          onChange={(v) => setCaps({ ...caps, doctor_can_do_reception: v })}
          testid="ta-cap-doctor-reception"
        />
        <Row
          icon={Pill}
          title="Doctors can also dispense medicines"
          desc="Useful for small clinics where the doctor hands out medicine themselves."
          value={caps.doctor_can_dispense}
          onChange={(v) => setCaps({ ...caps, doctor_can_dispense: v })}
          testid="ta-cap-doctor-dispense"
        />

        <div className="p-4 border rounded-lg flex items-start gap-4">
          <Stethoscope className="h-5 w-5 text-blue-700 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium text-zinc-900">Pharmacy module</div>
            <div className="text-xs text-zinc-500 mt-0.5">
              <b>Lite</b> = quick "dispense + collect" flow off prescriptions.<br />
              <b>Full</b> = the legacy pharmacy module with inventory / GRN / sales.
            </div>
          </div>
          <div className="w-32">
            <Select value={caps.pharmacy_mode || 'lite'}
              onValueChange={(v) => setCaps({ ...caps, pharmacy_mode: v })}>
              <SelectTrigger data-testid="ta-cap-pharmacy-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="lite">Lite</SelectItem>
                <SelectItem value="full">Full</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
        <Label className="text-xs">User-limit override (set by platform owner):</Label>
        <div className="mt-1 font-mono">
          {caps.max_users === null || caps.max_users === undefined
            ? 'inherits plan default'
            : caps.max_users === -1 ? 'unlimited' : caps.max_users}
        </div>
      </div>

      <Button onClick={save} disabled={saving}
        className="mt-6 bg-blue-700 hover:bg-blue-800" data-testid="ta-caps-save">
        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
        Save
      </Button>
    </div>
  );
}
